import type * as cleave from "cleave-zen";
import * as t from "io-ts";
import React, { type JSX } from "react";

import { TBasicStringFormatters } from "../utils/format";
import { WebPageURL } from "./nominals";
import { PartialRecord, RecordKeyType } from "./utils";

const BaseErrorSet = t.readonly(
    t.partial({
        code: t.string,
        detail: t.string,
        non_field_errors: t.readonlyArray(t.string),
    }),
);
type BaseErrorSet = t.TypeOf<typeof BaseErrorSet>;

export const ErrorFieldValue = t.union([
    t.string,
    t.readonlyArray(t.string),
    t.undefined,
]);
export type ErrorFieldValue = t.TypeOf<typeof ErrorFieldValue>;

export const ErrorSet = t.readonly(
    t.intersection([BaseErrorSet, t.record(t.string, ErrorFieldValue)]),
);
export type ErrorSet = t.TypeOf<typeof ErrorSet>;

export const GenericErrorSet = <T extends RecordKeyType>(
    fieldNames: T,
): t.ReadonlyC<
    t.IntersectionC<
        [
            typeof BaseErrorSet,
            t.PartialC<Record<keyof T["keys"], typeof ErrorFieldValue>>,
        ]
    >
> => {
    return t.readonly(
        t.intersection([
            BaseErrorSet,
            PartialRecord(fieldNames, ErrorFieldValue),
        ]),
    );
};
export type GenericErrorSet<T extends string> = BaseErrorSet & {
    [K in T]?: ErrorFieldValue;
};

/**
 * Supported kinds of field validation
 */
export const VALIDATION_TYPES = [
    "firstname-empty",
    "lastname-empty",
    "shipping-address-empty",
    "street-address-empty",
    "city-empty",
    "state-empty",
    "required",
    "nonzero",
    "lastname",
    "middle-initial",
    "email",
    "zipcode",
    "phone",
    "ssn",
    "ssn_last_four",
    "date",
    "credit-card",
    "expiration-date",
    "cvc",
    "cvc3",
    "cvc4",
    "amount",
    "financing-account",
    "annual-income-empty",
    "citizenship-empty",
] as const;

export type ValidationTypes =
    | (typeof VALIDATION_TYPES)[number]
    | ((value: string | boolean) => string | null);

type FormatCreditCardOptions = Readonly<
    cleave.FormatCreditCardOptions & {
        type: "credit-card";
    }
>;
type FormatDateOptions = Readonly<
    cleave.FormatDateOptions & {
        type: "date";
    }
>;
type FormatNumeralOptions = Readonly<
    cleave.FormatNumeralOptions & {
        type: "numeral";
    }
>;
type FormatGeneralOptions = Readonly<
    cleave.FormatGeneralOptions & {
        type: "general";
    }
>;
type FormatTimeOptions = Readonly<
    cleave.FormatTimeOptions & {
        type: "time";
    }
>;
export type FormatOptions =
    | FormatCreditCardOptions
    | FormatDateOptions
    | FormatNumeralOptions
    | FormatGeneralOptions
    | FormatTimeOptions;

/**
 * Abstract base interface for a form component
 */
export type IBaseComponent = Readonly<{
    id?: string;
    name?: string;
    className?: string;

    helpText?: string;
    helpTextClass?: string;

    tooltipLink?: string;
    tooltipText?: string;
    tooltipClass?: string;
    isIconTrigger?: boolean;

    warningTitle?: string;
    warningText?: string;

    onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
    onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;

    errors?: readonly string[];

    showErrorMessages?: boolean;

    ariaExpanded?: boolean;
}>;

export type IFormHelpLink = IBaseComponent &
    Readonly<{
        href: WebPageURL;
        target?: string;
    }>;

export type IFormButtonLink = IBaseComponent &
    Readonly<{
        href: WebPageURL;
        target?: string;
        text: string;
    }>;

type IFormButton<FieldName extends string> = IBaseComponent &
    Readonly<{
        name?: FieldName;
        type?: "submit" | "button";
        hidden?: boolean;
        value?: string;
        disabled?: boolean;
    }>;

export type IFormSubmitButton<FieldName extends string> =
    IFormButton<FieldName> &
        Readonly<{
            type?: "submit";
            inputRef?: React.RefObject<HTMLInputElement | null>;
            onSubmit?: (event: React.FormEvent<HTMLElement>) => void;
        }>;

/**
 * Abstract base interface for an form field.
 */
export type IBaseField<
    IInputType extends HTMLElement,
    FieldName extends string,
> = IBaseComponent &
    Readonly<{
        name: FieldName;

        type?:
            | "boolean"
            | "checkbox"
            | "radio"
            | "choice"
            | "radio-choice"
            | "integer"
            | "number"
            | "string"
            | "text"
            | "tel"
            | "date"
            | "datetime"
            | "email"
            | "password"
            | "textbox"
            | "fuzzy-duration"
            | "phone";
        autoComplete?: string;

        value?: string;
        defaultValue?: string;

        // Keep the DOM as the source of truth: `value` still feeds validation
        // and the initial render, but is never written back to the input.
        // For fields that receive machine-paced input (card readers), where a
        // controlled value round-trip can erase keystrokes.
        uncontrolled?: boolean;

        required?: boolean;
        readOnly?: boolean;
        disabled?: boolean;

        label?: string;
        hasHTML?: boolean;
        shortLabel?: string;
        labelPlacement?: "outside" | "dynamic" | "static";
        labelCSSClass?: string;
        wrapperCSSClass?: string;

        adaText?: string;

        placeholder?: string;

        validation?: ValidationTypes | readonly ValidationTypes[];
        pattern?: string;

        displayFormat?: TBasicStringFormatters;

        onChange?: (event: React.FormEvent<IInputType>) => void;
        onFocus?: (event: React.FormEvent<IInputType>) => void;
        onBlur?: (event: React.FormEvent<IInputType>) => void;
        onKeyPress?: (event: React.KeyboardEvent<IInputType>) => void;
        onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
        onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
        onValidStateChange?: (name: FieldName, errors: string[]) => void;

        secureImage?: Readonly<{ image: string; alt: string }>;

        // Ref to the underlying input element
        inputRef?: React.RefObject<IInputType | null>;
    }>;

/**
 * A true / false field usually represented by a check box.
 */
export type IBooleanField<FieldName extends string> = IBaseField<
    HTMLInputElement,
    FieldName
> &
    Readonly<{
        type?: "boolean" | "checkbox" | "radio";
        checked?: boolean;
    }>;

/**
 * A multiple-choice field represented by a select box.
 */
export type IChoiceField<FieldName extends string> = IBaseField<
    HTMLSelectElement,
    FieldName
> &
    Readonly<{
        type?: "choice";
        choices?: ReadonlyArray<
            Readonly<{
                label: string;
                value: string;
            }>
        >;
    }>;

/**
 * A multiple-choice field represented by a set of radio buttons box.
 */
export type IRadioChoiceField<FieldName extends string> = IBaseField<
    HTMLInputElement,
    FieldName
> &
    Readonly<{
        type?: "radio-choice";
        legend?: string;
        choices?: ReadonlyArray<
            Readonly<{
                value: string;
                label: string | JSX.Element;
                helpText?: string | JSX.Element;
                extraText?: string | JSX.Element;
                disabled?: boolean;
            }>
        >;
    }>;

/**
 * An integer input field.
 */
export type INumberField<FieldName extends string> = IBaseField<
    HTMLInputElement,
    FieldName
> &
    Readonly<{
        type?: "integer" | "number";
        min?: number;
        max?: number;
        step?: number | "any";
        formatOptions?: FormatOptions;
    }>;

/**
 * A short text / string input field.
 */
export type IStringField<FieldName extends string> = IBaseField<
    HTMLInputElement,
    FieldName
> &
    Readonly<{
        type?:
            | "string"
            | "text"
            | "tel"
            | "date"
            | "datetime"
            | "email"
            | "password";
        minLength?: number;
        maxLength?: number;
        maxBytes?: number;
        formatOptions?: FormatOptions;
    }>;

/**
 * A long text / string input field.
 */
export type ITextBoxField<FieldName extends string> = IBaseField<
    HTMLTextAreaElement,
    FieldName
> &
    Readonly<{
        type?: "textbox";
        rows?: number;
        cols?: number;
        minLength?: number;
        maxLength?: number;
    }>;

/**
 * A special string field for selecting a duration of time, with the format of MMYY.
 */
export type IFuzzyDurationField<FieldName extends string> = IBaseField<
    HTMLInputElement | HTMLSelectElement,
    FieldName
> &
    Readonly<{
        type?: "fuzzy-duration";
    }>;

/**
 * A special string field for entering formatted phone numbers.
 */
export type IPhoneNumberField<FieldName extends string> = IBaseField<
    HTMLInputElement,
    FieldName
> &
    Readonly<{
        type?: "phone";
    }>;

/**
 * Union type to represent any API field.
 */
export type IField<FieldName extends string> =
    | IBooleanField<FieldName>
    | IChoiceField<FieldName>
    | IRadioChoiceField<FieldName>
    | INumberField<FieldName>
    | IStringField<FieldName>
    | ITextBoxField<FieldName>
    | IFuzzyDurationField<FieldName>
    | IPhoneNumberField<FieldName>;

/**
 * A set of field definitions.
 */
export type IFieldSet<FieldNames extends string = string> = Readonly<{
    [T in FieldNames]: IField<FieldNames>;
}>;

/**
 * A set of field values.
 */
export type IValueSet<FieldNames extends string = string> = Readonly<{
    [T in FieldNames]: string | boolean | undefined;
}>;
